import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { getConfig, updateConfig, updatePins } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FloppyDisk, UserCircle, Plus, Minus, Lock, ShieldCheck, HardHat, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rolesEnabled, setRolesEnabled] = useState([]);
  const [pinAdmin, setPinAdmin] = useState("");
  const [pinOp, setPinOp] = useState("");
  const [pinCampo, setPinCampo] = useState("");
  const [savingPins, setSavingPins] = useState(false);

  useEffect(() => {
    getConfig().then((c) => {
      setData(c.agrimensor);
      setRolesEnabled(c.roles_enabled || []);
    }).catch(() => toast.error(t.error));
    // eslint-disable-next-line
  }, []);

  const upd = (f, v) => setData({ ...data, [f]: v });

  const save = async () => {
    setSaving(true);
    try {
      const c = await updateConfig(data);
      setData(c.agrimensor);
      setRolesEnabled(c.roles_enabled || []);
      toast.success(t.saved);
    } catch (e) { toast.error(t.error); }
    finally { setSaving(false); }
  };

  const savePins = async () => {
    // Build payload — empty string means "disable role"; null means "don't change"
    const payload = {};
    const validPin = (p) => /^\d{4,6}$/.test(p);
    if (pinAdmin) {
      if (!validPin(pinAdmin)) { toast.error("PIN admin inválido (4-6 dígitos)"); return; }
      payload.admin_pin = pinAdmin;
    }
    // Operativo: vacío explícito (con flag) deshabilita; queda fuera si no se tocó
    if (pinOp === "__DISABLE__") payload.operativo_pin = "";
    else if (pinOp) {
      if (!validPin(pinOp)) { toast.error("PIN operativo inválido (4-6 dígitos)"); return; }
      payload.operativo_pin = pinOp;
    }
    if (pinCampo === "__DISABLE__") payload.campo_pin = "";
    else if (pinCampo) {
      if (!validPin(pinCampo)) { toast.error("PIN campo inválido (4-6 dígitos)"); return; }
      payload.campo_pin = pinCampo;
    }
    if (Object.keys(payload).length === 0) { toast.info("Nada que guardar"); return; }
    setSavingPins(true);
    try {
      const res = await updatePins(payload);
      setRolesEnabled(res.roles_enabled || []);
      setPinAdmin(""); setPinOp(""); setPinCampo("");
      toast.success("PINs actualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando PINs");
    } finally { setSavingPins(false); }
  };

  if (!data) return <div className="p-10 text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="profile-page">
      <button
        data-testid="back-btn"
        onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-5"
      >
        <ArrowLeft size={14} /> {t.back}
      </button>
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-6">
        <div>
          <div className="coord-label mb-1.5">// MY PROFILE</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 flex items-center gap-3">
            <UserCircle weight="duotone" size={36} color="#f97316" />
            {t.profile_title}
          </h1>
          <p className="text-slate-600 mt-1">{t.profile_desc}</p>
        </div>
        <Button
          data-testid="save-profile-btn"
          onClick={save} disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
        >
          <FloppyDisk weight="duotone" size={16} className="mr-1.5" />
          {saving ? "…" : t.save}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div className="md:col-span-2">
          <Label className="coord-label">{t.profile_nombre}</Label>
          <Input data-testid="prof-nombre" value={data.nombre} onChange={(e) => upd("nombre", e.target.value)} className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_nacionalidad}</Label>
          <Input data-testid="prof-nacionalidad" value={data.nacionalidad} onChange={(e) => upd("nacionalidad", e.target.value)} className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_sexo}</Label>
          <Select value={data.sexo} onValueChange={(v) => upd("sexo", v)}>
            <SelectTrigger data-testid="prof-sexo" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Masculino / Male</SelectItem>
              <SelectItem value="F">Femenino / Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="coord-label">{t.profile_estado_civil}</Label>
          <Input data-testid="prof-estado" value={data.estado_civil} onChange={(e) => upd("estado_civil", e.target.value)} className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_cedula}</Label>
          <Input data-testid="prof-cedula" value={data.cedula} onChange={(e) => upd("cedula", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_codia}</Label>
          <Input data-testid="prof-codia" value={data.codia} onChange={(e) => upd("codia", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_tel1}</Label>
          <Input data-testid="prof-tel1" value={data.telefono1} onChange={(e) => upd("telefono1", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
        </div>
        <div>
          <Label className="coord-label">{t.profile_tel2}</Label>
          <Input data-testid="prof-tel2" value={data.telefono2} onChange={(e) => upd("telefono2", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
        </div>
        <div className="md:col-span-2">
          <Label className="coord-label">{t.profile_email}</Label>
          <Input data-testid="prof-email" value={data.email} onChange={(e) => upd("email", e.target.value)} className="mt-1.5 rounded-sm" />
        </div>
        <div className="md:col-span-2">
          <Label className="coord-label">{t.profile_direccion}</Label>
          <Input data-testid="prof-direccion" value={data.direccion} onChange={(e) => upd("direccion", e.target.value)} className="mt-1.5 rounded-sm" />
        </div>
      </div>

      {/* Logo y firma escaneada */}
      <div className="mt-6 bg-white border border-slate-200 p-6">
        <div className="mb-4">
          <div className="coord-label">// IDENTIDAD VISUAL EN PDFs</div>
          <h3 className="text-lg font-bold tracking-tight text-slate-900">Logo y firma escaneada</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Sube tu logo (membrete) y tu firma escaneada (fondo blanco o transparente). Aparecerán automáticamente en los PDFs que generes.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploader
            label="Logo / Membrete"
            value={data.logo_data_url}
            onChange={(v) => upd("logo_data_url", v)}
            testId="prof-logo"
            hint="PNG/JPG horizontal, fondo blanco recomendado. Máx 1 MB."
          />
          <ImageUploader
            label="Firma escaneada"
            value={data.firma_data_url}
            onChange={(v) => upd("firma_data_url", v)}
            testId="prof-firma"
            hint="PNG con fondo transparente recomendado. Máx 500 KB."
          />
        </div>
      </div>

      {/* Acta de Hitos defaults */}
      <div className="mt-6 bg-white border border-slate-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// ACTA DE HITOS — DEFAULTS</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900">Valores por defecto para el Acta</h3>
            <p className="text-sm text-slate-500 mt-0.5">Equipo, instrumento y método que se cargan automáticamente al pulsar &quot;Aplicar valores del Perfil&quot;.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <Label className="coord-label">Instrumento — Marca</Label>
            <Input
              data-testid="prof-inst-marca"
              value={data.instrumento_marca || ""}
              onChange={(e) => upd("instrumento_marca", e.target.value)}
              placeholder="RECEPTOR TOPCON Y ESTACION TOTAL TOPCON"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div>
            <Label className="coord-label">Instrumento — Modelo</Label>
            <Input
              data-testid="prof-inst-modelo"
              value={data.instrumento_modelo || ""}
              onChange={(e) => upd("instrumento_modelo", e.target.value)}
              placeholder="HIPER LITE PLUS"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div>
            <Label className="coord-label">Método de levantamiento</Label>
            <Select value={data.metodo_levantamiento_default || ""} onValueChange={(v) => upd("metodo_levantamiento_default", v)}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="prof-metodo-lev"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Sencillo">Sencillo (un set)</SelectItem>
                <SelectItem value="Repetitivo">Repetitivo (más de un set)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="coord-label">Cantidad de sets (si Repetitivo)</Label>
            <Input
              data-testid="prof-sets"
              value={data.cantidad_sets_default || ""}
              onChange={(e) => upd("cantidad_sets_default", e.target.value)}
              placeholder="Ej. 2"
              className="mt-1.5 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="coord-label">Dirección de la medición</Label>
            <Select value={data.direccion_medicion_default || ""} onValueChange={(v) => upd("direccion_medicion_default", v)}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="prof-dir-med"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Directo">Directo (radiación)</SelectItem>
                <SelectItem value="Inverso">Inverso (deflexión)</SelectItem>
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="coord-label">Método de orientación</Label>
            <Input
              data-testid="prof-orient"
              value={data.metodo_orientacion_default || ""}
              onChange={(e) => upd("metodo_orientacion_default", e.target.value)}
              placeholder="NTRIP / Astronómico / Magnético"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="coord-label">Equipo de campo (texto libre)</Label>
            <Textarea
              data-testid="prof-equipo"
              value={data.equipo_campo || ""}
              onChange={(e) => upd("equipo_campo", e.target.value)}
              placeholder="Equipo / brigada habitual"
              className="mt-1.5 rounded-sm min-h-[60px]"
            />
          </div>

          {/* Personas con cargo */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="coord-label">Personas auxiliares por defecto (nombre + cargo)</Label>
              <Button
                size="sm" variant="outline"
                onClick={() => upd("personas_campo_default", [...(data.personas_campo_default || []), { nombre: "", cargo: "" }])}
                className="rounded-sm"
              >
                <Plus size={12} className="mr-1" />Agregar
              </Button>
            </div>
            {(!data.personas_campo_default || data.personas_campo_default.length === 0) && (
              <div className="text-sm text-slate-500 py-2">Sin personas configuradas.</div>
            )}
            <div className="space-y-2">
              {(data.personas_campo_default || []).map((p, i) => {
                const personObj = typeof p === "string" ? { nombre: p, cargo: "" } : p;
                const list = (data.personas_campo_default || []).map((x) => (typeof x === "string" ? { nombre: x, cargo: "" } : x));
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <span className="font-mono text-xs text-slate-500 col-span-1">#{i + 1}</span>
                    <Input
                      value={personObj.nombre || ""}
                      onChange={(e) => {
                        const next = [...list];
                        next[i] = { ...next[i], nombre: e.target.value };
                        upd("personas_campo_default", next);
                      }}
                      placeholder="Nombre y Apellidos"
                      className="col-span-7 rounded-sm"
                    />
                    <Input
                      value={personObj.cargo || ""}
                      onChange={(e) => {
                        const next = [...list];
                        next[i] = { ...next[i], cargo: e.target.value };
                        upd("personas_campo_default", next);
                      }}
                      placeholder="Cargo (Ej. ENCARGADO DE BRIGADA)"
                      className="col-span-3 rounded-sm"
                    />
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => upd("personas_campo_default", list.filter((_, idx) => idx !== i))}
                      className="col-span-1 rounded-sm text-red-500"
                    >
                      <Minus size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Defaults del Acta de Hitos */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="acta-defaults-section">
        <div className="mb-4">
          <div className="coord-label">// DEFAULTS ACTA DE HITOS</div>
          <h3 className="text-lg font-bold tracking-tight text-slate-900">
            Valores por Defecto del Acta de Hitos
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Estos valores se pre-cargan automáticamente al crear un expediente nuevo. Puedes editarlos por expediente sin afectar el default global.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {/* Hitos pre-definidos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="coord-label">Hitos de vinculación (PG-XX) pre-cargados</Label>
              <Button
                data-testid="add-hito-default-btn"
                size="sm" variant="outline"
                onClick={() => upd("hitos_default", [
                  ...(data.hitos_default || []),
                  { estacion: `PG-${String((data.hitos_default || []).length + 1).padStart(2, "0")}`, materializacion: "Clavo Topográfico", observaciones: "Punto Georreferenciado" }
                ])}
                className="rounded-sm h-7"
              >
                <Plus size={12} className="mr-1" />Agregar hito
              </Button>
            </div>
            {(!data.hitos_default || data.hitos_default.length === 0) && (
              <div className="text-sm text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
                Sin hitos por defecto. Agrega PG-01 y PG-02 con sus materializaciones habituales.
              </div>
            )}
            <div className="space-y-1.5">
              {(data.hitos_default || []).map((h, i) => {
                const list = data.hitos_default || [];
                return (
                  <div key={i} className="grid grid-cols-12 gap-2" data-testid={`hito-default-row-${i}`}>
                    <Input
                      data-testid={`hito-estacion-${i}`}
                      value={h.estacion || ""}
                      onChange={(e) => { const next = [...list]; next[i] = { ...next[i], estacion: e.target.value }; upd("hitos_default", next); }}
                      placeholder="PG-01"
                      className="col-span-2 rounded-sm font-mono text-sm"
                    />
                    <Input
                      data-testid={`hito-mat-${i}`}
                      value={h.materializacion || ""}
                      onChange={(e) => { const next = [...list]; next[i] = { ...next[i], materializacion: e.target.value }; upd("hitos_default", next); }}
                      placeholder="Clavo Topográfico"
                      className="col-span-4 rounded-sm"
                    />
                    <Input
                      data-testid={`hito-obs-${i}`}
                      value={h.observaciones || ""}
                      onChange={(e) => { const next = [...list]; next[i] = { ...next[i], observaciones: e.target.value }; upd("hitos_default", next); }}
                      placeholder="Punto Georreferenciado"
                      className="col-span-5 rounded-sm"
                    />
                    <Button
                      data-testid={`hito-del-${i}`}
                      size="sm" variant="ghost"
                      onClick={() => upd("hitos_default", list.filter((_, idx) => idx !== i))}
                      className="col-span-1 rounded-sm text-red-500"
                    >
                      <Minus size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Información Adicional default */}
          <div>
            <Label className="coord-label">Información Adicional (texto por defecto)</Label>
            <Textarea
              data-testid="prof-info-adicional"
              value={data.informacion_adicional_default || ""}
              onChange={(e) => upd("informacion_adicional_default", e.target.value)}
              placeholder="Ej: LA GEORREFERENCIACION DE LOS PUNTOS SE REALIZÓ UTILIZANDO EL METODO NTRIP, LA NOTIFICACION A LA DIRECCIÓN REGIONAL DE MENSURAS CATASTRALES SE REALIZÓ DE FORMA ESCRITA..."
              className="mt-1.5 rounded-sm min-h-[100px] text-sm"
            />
          </div>
        </div>
      </div>

      {/* Instrumentos utilizados en campo (perfiles) */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="instrumentos-section">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// INSTRUMENTOS DE CAMPO</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Wrench weight="duotone" size={20} color="#f97316" />
              Perfiles de Instrumentos
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Registra los equipos que usas en campo (Estación Total, Receptor GNSS, etc.). El primero
              de cada tipo se usa automáticamente al generar el Informe Técnico y demás documentos.
            </p>
          </div>
          <Button
            data-testid="add-instrumento-btn"
            size="sm" variant="outline"
            onClick={() => upd("instrumentos_perfiles", [
              ...(data.instrumentos_perfiles || []),
              { id: `inst_${Date.now()}`, tipo: "estacion_total", marca: "", modelo: "" }
            ])}
            className="rounded-sm"
          >
            <Plus size={12} className="mr-1" />Agregar instrumento
          </Button>
        </div>

        {(!data.instrumentos_perfiles || data.instrumentos_perfiles.length === 0) && (
          <div className="text-sm text-slate-500 py-4 border border-dashed border-slate-300 rounded-sm text-center">
            Sin instrumentos configurados. Agrega tu Estación Total y Receptor GNSS para que aparezcan
            en el Informe Técnico.
          </div>
        )}

        <div className="space-y-2">
          {(data.instrumentos_perfiles || []).map((inst, i) => {
            const list = data.instrumentos_perfiles || [];
            return (
              <div key={inst.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`instrumento-row-${i}`}>
                <span className="font-mono text-xs text-slate-500 col-span-1">#{i + 1}</span>
                <div className="col-span-3">
                  <Select
                    value={inst.tipo || "estacion_total"}
                    onValueChange={(v) => {
                      const next = [...list];
                      next[i] = { ...next[i], tipo: v };
                      upd("instrumentos_perfiles", next);
                    }}
                  >
                    <SelectTrigger className="rounded-sm" data-testid={`inst-tipo-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="estacion_total">Estación Total</SelectItem>
                      <SelectItem value="receptor_gnss">Receptor GNSS</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  data-testid={`inst-marca-${i}`}
                  value={inst.marca || ""}
                  onChange={(e) => {
                    const next = [...list];
                    next[i] = { ...next[i], marca: e.target.value };
                    upd("instrumentos_perfiles", next);
                  }}
                  placeholder="Marca (ej. Topcon)"
                  className="col-span-4 rounded-sm"
                />
                <Input
                  data-testid={`inst-modelo-${i}`}
                  value={inst.modelo || ""}
                  onChange={(e) => {
                    const next = [...list];
                    next[i] = { ...next[i], modelo: e.target.value };
                    upd("instrumentos_perfiles", next);
                  }}
                  placeholder="Modelo (ej. GTS 225 / Hiper Lite Plus)"
                  className="col-span-3 rounded-sm"
                />
                <Button
                  data-testid={`inst-del-${i}`}
                  size="sm" variant="ghost"
                  onClick={() => upd("instrumentos_perfiles", list.filter((_, idx) => idx !== i))}
                  className="col-span-1 rounded-sm text-red-500"
                >
                  <Minus size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Notario por Defecto */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="notario-default-section">
        <div className="mb-4">
          <div className="coord-label">// NOTARIO POR DEFECTO</div>
          <h3 className="text-lg font-bold tracking-tight text-slate-900">
            Datos del Notario
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Si trabajas habitualmente con el mismo notario, se autocompletará en cada expediente
            nuevo. Puedes cambiarlo por caso desde el tab "Datos" del expediente.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="coord-label">Nombre del Notario</Label>
            <Input
              data-testid="prof-notario-nombre"
              value={(data.notario_default || {}).nombre || ""}
              onChange={(e) => upd("notario_default", { ...(data.notario_default || {}), nombre: e.target.value })}
              placeholder="DR. ARCADIO CIPION MONTERO"
              className="mt-1 rounded-sm"
            />
          </div>
          <div>
            <Label className="coord-label">Matrícula</Label>
            <Input
              data-testid="prof-notario-matricula"
              value={(data.notario_default || {}).matricula || ""}
              onChange={(e) => upd("notario_default", { ...(data.notario_default || {}), matricula: e.target.value })}
              placeholder="1234"
              className="mt-1 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="coord-label">Municipio del Colegio (Jurisdicción)</Label>
            <Input
              data-testid="prof-notario-jurisdiccion"
              value={(data.notario_default || {}).municipio_colegio || (data.notario_default || {}).jurisdiccion || ""}
              onChange={(e) => upd("notario_default", { ...(data.notario_default || {}), municipio_colegio: e.target.value })}
              placeholder="Distrito Nacional"
              className="mt-1 rounded-sm"
            />
          </div>
          <div>
            <Label className="coord-label">Provincia del Notario</Label>
            <Input
              data-testid="prof-notario-provincia"
              value={(data.notario_default || {}).provincia || ""}
              onChange={(e) => upd("notario_default", { ...(data.notario_default || {}), provincia: e.target.value })}
              placeholder="Santo Domingo / San Cristóbal / ..."
              className="mt-1 rounded-sm"
            />
          </div>
        </div>
      </div>

      {/* Testigos por Defecto (para trámite de Duplicado por Pérdida) */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="testigos-section">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// TESTIGOS — DUPLICADO POR PÉRDIDA</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900">
              Testigos por Defecto (Duplicado)
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Estos testigos aparecen automáticamente en los actos notariales del trámite de
              "Duplicado por Pérdida". Puedes sobrescribirlos por expediente cuando sea necesario.
            </p>
          </div>
          <Button
            data-testid="add-testigo-default-btn"
            size="sm" variant="outline"
            onClick={() => upd("testigos_default", [
              ...(data.testigos_default || []),
              { nombre: "", nacionalidad: "dominicano", estado_civil: "soltero", cedula: "", domicilio: "" }
            ])}
            className="rounded-sm"
          >
            <Plus size={12} className="mr-1" />Agregar testigo
          </Button>
        </div>

        {(!data.testigos_default || data.testigos_default.length === 0) && (
          <div className="text-sm text-slate-500 py-4 border border-dashed border-slate-300 rounded-sm text-center">
            Sin testigos configurados. Agrega los 2 que sueles usar en tus actos notariales.
          </div>
        )}

        <div className="space-y-3">
          {(data.testigos_default || []).map((t, i) => {
            const list = data.testigos_default || [];
            return (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`testigo-default-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">TESTIGO #{i + 1}</span>
                  <Button
                    data-testid={`testigo-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("testigos_default", list.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  >
                    <Minus size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`testigo-nombre-${i}`}
                    value={t.nombre || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("testigos_default", next);
                    }}
                    placeholder="Nombre completo (ej. ANDERSON CARABALLO)"
                    className="rounded-sm md:col-span-2"
                  />
                  <Input
                    data-testid={`testigo-cedula-${i}`}
                    value={t.cedula || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("testigos_default", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`testigo-estado-${i}`}
                    value={t.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("testigos_default", next);
                    }}
                    placeholder="Estado civil (soltero/soltera/casado...)"
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`testigo-domicilio-${i}`}
                    value={t.domicilio || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("testigos_default", next);
                    }}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Testigos por Defecto (Posesión / Mejora) */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="testigos-posesion-section">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// TESTIGOS — POSESIÓN / MEJORA</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900">
              Testigos por Defecto (Posesión / Mejora)
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Estos 7 testigos típicos aparecen automáticamente en la Declaración Jurada de Posesión y Mejora. Puedes sobrescribirlos por expediente.
            </p>
          </div>
          <Button
            data-testid="add-testigo-posesion-btn"
            size="sm" variant="outline"
            onClick={() => upd("testigos_posesion_default", [
              ...(data.testigos_posesion_default || []),
              { nombre: "", nacionalidad: "dominicano", estado_civil: "soltero", cedula: "", domicilio: "" }
            ])}
            className="rounded-sm"
          >
            <Plus size={12} className="mr-1" />Agregar testigo
          </Button>
        </div>

        {(!data.testigos_posesion_default || data.testigos_posesion_default.length === 0) && (
          <div className="text-sm text-slate-500 py-4 border border-dashed border-slate-300 rounded-sm text-center">
            Sin testigos configurados. Agrega los 7 testigos típicos para Posesión/Mejora.
          </div>
        )}

        <div className="space-y-3">
          {(data.testigos_posesion_default || []).map((t, i) => {
            const list = data.testigos_posesion_default || [];
            return (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`testigo-posesion-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">TESTIGO #{i + 1}</span>
                  <Button
                    data-testid={`testigo-posesion-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("testigos_posesion_default", list.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  >
                    <Minus size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`testigo-posesion-nombre-${i}`}
                    value={t.nombre || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("testigos_posesion_default", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm md:col-span-2"
                  />
                  <Input
                    data-testid={`testigo-posesion-cedula-${i}`}
                    value={t.cedula || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("testigos_posesion_default", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`testigo-posesion-estado-${i}`}
                    value={t.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("testigos_posesion_default", next);
                    }}
                    placeholder="Estado civil (soltero/soltera/casado...)"
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`testigo-posesion-domicilio-${i}`}
                    value={t.domicilio || ""}
                    onChange={(e) => {
                      const next = [...list]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("testigos_posesion_default", next);
                    }}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Plantillas de Poder de Representación */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="poder-plantillas-section">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// PODER DE REPRESENTACIÓN</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900">
              Plantillas del Cuerpo PRIMERO
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Textos reutilizables del objeto del Poder. Aparecerán como opciones rápidas al editar un Poder en el expediente. Usa <code className="bg-slate-100 px-1 rounded text-xs">{"{lugar_inmueble}"}</code> como marcador del lugar.
            </p>
          </div>
          <Button
            data-testid="add-poder-plantilla-btn"
            size="sm" variant="outline"
            onClick={() => upd("poder_plantillas", [
              ...(data.poder_plantillas || []),
              { nombre: "", texto: "" }
            ])}
            className="rounded-sm"
          >
            <Plus size={12} className="mr-1" />Agregar plantilla
          </Button>
        </div>

        {(!data.poder_plantillas || data.poder_plantillas.length === 0) && (
          <div className="text-sm text-slate-500 py-4 border border-dashed border-slate-300 rounded-sm text-center">
            Sin plantillas. Agrega textos como "Para Registro de Título", "Para IAD", "Para venta", etc.
          </div>
        )}

        <div className="space-y-3">
          {(data.poder_plantillas || []).map((p, i) => {
            const list = data.poder_plantillas || [];
            return (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`poder-plantilla-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">PLANTILLA #{i + 1}</span>
                  <Button
                    data-testid={`poder-plantilla-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("poder_plantillas", list.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  >
                    <Minus size={14} />
                  </Button>
                </div>
                <Input
                  data-testid={`poder-plantilla-nombre-${i}`}
                  value={p.nombre || ""}
                  onChange={(e) => {
                    const next = [...list]; next[i] = { ...next[i], nombre: e.target.value };
                    upd("poder_plantillas", next);
                  }}
                  placeholder='Nombre de la plantilla (ej: "Para Registro de Título")'
                  className="rounded-sm font-semibold mb-2"
                />
                <Textarea
                  data-testid={`poder-plantilla-texto-${i}`}
                  value={p.texto || ""}
                  onChange={(e) => {
                    const next = [...list]; next[i] = { ...next[i], texto: e.target.value };
                    upd("poder_plantillas", next);
                  }}
                  placeholder='Texto del PRIMERO. Ej: "EL PODERDANTE autoriza al APODERADO, a solicitar..."'
                  className="rounded-sm font-mono text-xs min-h-[120px]"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* PIN management (admin only) */}
      <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="pin-section">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="coord-label">// ACCESOS Y PINS</div>
            <h3 className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Lock weight="duotone" size={20} color="#f97316" />
              Gestión de PINs por rol
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Cambia o crea los PINs de Administrador (tú), Operativo y Campo. Deja en blanco un campo para no modificarlo. Para deshabilitar un rol completo, usa el botón "Deshabilitar".
            </p>
          </div>
          <Button
            data-testid="save-pins-btn"
            onClick={savePins}
            disabled={savingPins}
            className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
          >
            <FloppyDisk weight="duotone" size={14} className="mr-1.5" />
            {savingPins ? "…" : "Actualizar PINs"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Admin */}
          <div className="border border-slate-200 p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck weight="duotone" size={18} className="text-orange-500" />
              <div className="font-bold text-sm">Administrador</div>
              <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">ACTIVO</span>
            </div>
            <p className="text-xs text-slate-500 mb-2">Acceso total (montos, contrato, PINs).</p>
            <Input
              data-testid="pin-admin-input"
              type="password" inputMode="numeric" autoComplete="off"
              value={pinAdmin}
              onChange={(e) => setPinAdmin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Nuevo PIN (4-6 dígitos)"
              className="rounded-sm font-mono tracking-widest"
            />
          </div>

          {/* Operativo */}
          <div className="border border-slate-200 p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <UserCircle weight="duotone" size={18} className="text-blue-500" />
              <div className="font-bold text-sm">Operativo</div>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${rolesEnabled.includes("operativo") ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {rolesEnabled.includes("operativo") ? "ACTIVO" : "INACTIVO"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-2">Todo excepto montos y contrato.</p>
            <Input
              data-testid="pin-op-input"
              type="password" inputMode="numeric" autoComplete="off"
              value={pinOp === "__DISABLE__" ? "" : pinOp}
              onChange={(e) => setPinOp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Nuevo PIN (4-6 dígitos)"
              className="rounded-sm font-mono tracking-widest"
            />
            {rolesEnabled.includes("operativo") && (
              <button
                type="button"
                onClick={() => setPinOp("__DISABLE__")}
                className="text-[11px] text-red-600 hover:underline mt-1"
              >
                {pinOp === "__DISABLE__" ? "Se deshabilitará al guardar" : "Deshabilitar acceso"}
              </button>
            )}
          </div>

          {/* Campo */}
          <div className="border border-slate-200 p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <HardHat weight="duotone" size={18} className="text-emerald-500" />
              <div className="font-bold text-sm">Campo</div>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${rolesEnabled.includes("campo") ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {rolesEnabled.includes("campo") ? "ACTIVO" : "INACTIVO"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-2">Solo edita Acta de Hitos y Fotos.</p>
            <Input
              data-testid="pin-campo-input"
              type="password" inputMode="numeric" autoComplete="off"
              value={pinCampo === "__DISABLE__" ? "" : pinCampo}
              onChange={(e) => setPinCampo(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Nuevo PIN (4-6 dígitos)"
              className="rounded-sm font-mono tracking-widest"
            />
            {rolesEnabled.includes("campo") && (
              <button
                type="button"
                onClick={() => setPinCampo("__DISABLE__")}
                className="text-[11px] text-red-600 hover:underline mt-1"
              >
                {pinCampo === "__DISABLE__" ? "Se deshabilitará al guardar" : "Deshabilitar acceso"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageUploader({ label, value, onChange, testId, hint }) {
  const fileRef = useRef(null);
  const onPick = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Solo imágenes"); return; }
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Máx 1.5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div>
      <Label className="coord-label">{label}</Label>
      <div
        data-testid={testId}
        className="mt-1.5 border-2 border-dashed border-slate-300 rounded-sm p-3 flex items-center gap-3 bg-slate-50"
      >
        {value ? (
          <img src={value} alt={label} className="h-16 w-auto max-w-[180px] object-contain bg-white border border-slate-200 rounded-sm" />
        ) : (
          <div className="h-16 w-32 bg-white border border-slate-200 rounded-sm flex items-center justify-center text-xs text-slate-400 font-mono">vacío</div>
        )}
        <div className="flex-1">
          <p className="text-xs text-slate-500 mb-1.5">{hint}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onPick} className="rounded-sm" data-testid={`${testId}-pick`}>
              {value ? "Cambiar" : "Subir"}
            </Button>
            {value && (
              <Button size="sm" variant="ghost" onClick={() => onChange("")} className="rounded-sm text-red-500" data-testid={`${testId}-clear`}>
                Quitar
              </Button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        </div>
      </div>
    </div>
  );
}
